import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CAL_BOOKING_URL, GUIDE_DOWNLOAD_URL } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Clock, ArrowRight, Heart, Sparkles, Sun, Star, Quote, BadgeCheck, GraduationCap, Shield, Dumbbell, Download, Loader2, CheckCircle2, BookOpen } from "lucide-react";

const SERVICE_DETAILS = {
  "InAlign Transformation Coaching": {
    tagline: "Personalized coaching. Group accountability. Sustainable transformation.",
    overview: [
      "Get the structure and personal guidance you need to reach your goals—without doing it alone. You'll receive a personalized workout and nutrition plan, a 1-hour private onboarding session, weekly progress check-ins, and individualized coaching feedback from me every Monday. You'll also have access to our supportive coaching community and live educational sessions where you can learn, ask questions, and grow alongside others.",
    ],
    includes: [
      "1-hour private onboarding call",
      "Personalized workout program",
      "Individualized nutrition and macro guidance",
      "Weekly progress check-ins",
      "Personalized Monday coaching review and adjustments",
      "App access for nutrition, workouts, weight, measurements, and progress photos",
      "Community coaching and accountability",
      "Unlimited community messaging and support",
      "3 live educational Zoom calls each month",
    ],
    closing: "This isn't a one-size-fits-all program. Your coaching is built around you, your goals, your lifestyle, and your progress.",
    note: "Start with a free 30-minute discovery call to see if InAlign Transformation Coaching is the right fit for you.",
  },
  "InAlign Transformation VIP — 4 Months": {
    tagline: "A high-touch, private 4-month body recomposition coaching experience built entirely around you.",
    overview: [
      "Together, we'll create a personalized strategy to help you build lean muscle, reduce body fat, improve your nutrition, and develop the discipline and habits needed for lasting results.",
      "You'll receive fully customized training and nutrition guidance, weekly private accountability, personalized progress reviews, and direct access to me throughout your coaching journey. I'll review your progress, identify what's holding you back, make adjustments when needed, and help you stay on track every step of the way.",
    ],
    includes: [
      "Fully personalized workout program",
      "Individualized nutrition and macro guidance",
      "1-hour private onboarding call",
      "Weekly private check-ins and progress reviews",
      "Personalized coaching adjustments based on your progress",
      "Direct 1:1 messaging with me",
      "Private accountability and support",
      "Full access to the InAlign coaching app",
      "Personalized guidance to help you navigate challenges, travel, weekends, and real life",
      "3 private 45-minute Zoom coaching calls with me each month for deeper coaching",
    ],
    closing: "This is for anyone who is ready for a higher level of accountability, personal attention, and support — and wants a plan built specifically around them.",
    note: "Start with a free 30-minute discovery call to discuss your goals and determine if VIP coaching is the right fit for you.",
  },
  "InAlign Transformation VIP — 6 Months": {
    tagline: "A deeper 6-month transformation experience built around you.",
    overview: [
      "A deeper 6-month transformation experience designed to help you build a stronger body, reduce body fat, and create habits that last long after coaching ends.",
      "With six months of high-touch, personalized coaching, we'll have the time to build your foundation, progressively develop your strength and nutrition habits, work through challenges, and continually adjust your plan as your body, goals, and lifestyle evolve. You'll receive fully personalized training and nutrition guidance, private weekly check-ins, individualized progress reviews, and direct 1:1 support from me throughout your transformation.",
    ],
    includes: [
      "Fully personalized workout program",
      "Individualized nutrition and macro guidance",
      "1-hour private onboarding call",
      "Weekly private check-ins and progress reviews",
      "Personalized plan adjustments as you progress",
      "Direct 1:1 messaging with me",
      "Private accountability and support",
      "Full access to the InAlign coaching app",
      "Personalized guidance for navigating real-life challenges and staying consistent",
      "3 private 45-minute Zoom coaching calls with me each month for deeper coaching",
    ],
    closing: "Six months gives us the time to go beyond short-term results and build a stronger, more disciplined lifestyle that you can maintain.",
    note: "Start with a free 30-minute discovery call to discuss your goals and determine if VIP coaching is the right fit for you.",
  },
};


const TESTIMONIALS = [
  {
    name: "Jess M.",
    text: "Inna is incredibly attentive and creative in adapting every exercise so I could train safely and still make progress. Thanks to her, my balance improved, I got stronger, and I felt supported every step of the way. A rare combination of skilled, caring, and genuinely invested. Highly recommend.",
  },
  {
    name: "Helen A.",
    text: "Every morning started with a clear message and simple, realistic goals focused on mindset, nutrition, and movement. What stood out was how engaged and supportive Inna was — you can tell she truly cares about helping people build healthier habits in a way that feels realistic and sustainable.",
  },
  {
    name: "Natalia Minnick",
    text: "Her approach isn't about starving yourself or obsessing over calories — it's about balance, moderation, movement, protein, hydration, and building sustainable habits. She genuinely cares, keeps things real, and makes the whole process feel encouraging instead of overwhelming.",
  },
  {
    name: "Captain Pri",
    text: "What stood out most was how supportive, motivating, and realistic the approach felt. It wasn't about perfection — it was about building healthier habits, improving mindset, and creating consistency in a way that actually felt sustainable.",
  },
  {
    name: "Karen Minor",
    text: "Working with Inna was an exceptional experience. Her positivity and encouragement kept me motivated every step of the way. She shared valuable insights on hydration, nutrition, and consistency that made a meaningful and lasting impact.",
  },
  {
    name: "Ariauna Chandler",
    text: "InAlign Wellness is amazing! Inna is very interactive and encouraging when trying to reach your goals. I'm excited for future challenges that push me to be a better me!",
  },
];

export default function Landing() {
  const [services, setServices] = useState([]);
  const [guide, setGuide] = useState({ name: "", email: "" });
  const [guideSubmitting, setGuideSubmitting] = useState(false);
  const [guideDone, setGuideDone] = useState(false);
  const [guideDownloadUrl, setGuideDownloadUrl] = useState("");

  const submitGuide = async (e) => {
    e.preventDefault();
    setGuideSubmitting(true);
    try {
      await api.post("/leads", guide);
      setGuideDownloadUrl(GUIDE_DOWNLOAD_URL);
      setGuideDone(true);
      toast.success("Check your inbox — your guide is on the way!");
      const a = document.createElement("a");
      a.href = GUIDE_DOWNLOAD_URL;
      a.download = "The-Discipline-Code.pdf";
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.click();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setGuideSubmitting(false);
    }
  };

  useEffect(() => {
    api
      .get("/services", { params: { _t: Date.now() }, headers: { "Cache-Control": "no-cache" } })
      .then((r) => setServices(r.data))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="text-center lg:text-left"
          >
            <p className="text-sm uppercase tracking-[0.2em] text-[#556B2F] mb-6">Nutrition &amp; Fitness Coaching</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95] tracking-tight text-[#33311F]">
              Build discipline.<br />Stay consistent.<br /><span className="italic text-[#C0714A]">Be fit for life.</span>
            </h1>
            <p className="mt-8 text-lg text-[#5C5848] leading-relaxed max-w-md mx-auto lg:mx-0">
              One-on-one coaching built on discipline and consistency — helping you build lean muscle, burn body fat,
              and stay fit for life.
            </p>
            <div className="mt-10 flex items-center justify-center lg:justify-start gap-4">
              <a
                href={CAL_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-button"
                className="bg-[#556B2F] hover:bg-[#44531F] text-white rounded-full px-8 py-3.5 transition-all hover:-translate-y-0.5 flex items-center gap-2"
              >
                Book a Discovery Call <ArrowRight className="w-4 h-4" />
              </a>
              <a href="#services" className="text-[#33311F] underline underline-offset-4 decoration-[#C0714A]">
                Explore programs
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_book-slot-5/artifacts/5f70b149d52ed958_9H4A0891.webp"
              alt="InAlign Wellness coach in the gym"
              className="rounded-3xl w-full h-auto object-contain bg-[#E9E0CC] shadow-[0_20px_60px_rgba(60,55,40,0.15)]"
            />
          </motion.div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 pt-2 pb-8">
        <div className="grid sm:grid-cols-3 gap-8">
          {[
            { icon: Sun, title: "Disciplined", text: "Simple systems that build daily discipline that lasts." },
            { icon: Heart, title: "Personal", text: "Nutrition and training shaped around you and your goals." },
            { icon: Sparkles, title: "Consistent", text: "Accountability that keeps you showing up, week after week." },
          ].map((p, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="bg-[#556B2F]/15 rounded-full p-3">
                <p.icon className="w-5 h-5 text-[#556B2F]" strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif text-2xl text-[#33311F]">{p.title}</h3>
                <p className="text-[#5C5848] text-sm mt-1">{p.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* About the coach */}
      <section id="about" className="max-w-6xl mx-auto px-6 pt-12 pb-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="absolute -top-6 -left-6 w-28 h-28 rounded-3xl bg-[#556B2F]/12"></div>
            <div className="absolute -bottom-8 -right-6 w-44 h-44 rounded-full bg-[#C0714A]/12"></div>
            <div className="relative bg-white border border-[#DAD0BA] rounded-3xl p-4 shadow-[0_24px_70px_rgba(60,55,40,0.14)]">
              <img
                src="https://customer-assets-wrfwihn1.emergentagent.net/job_book-slot-5/artifacts/1f140b80d41d068e_9H4A1171-2.webp"
                alt="InAlign Wellness coach"
                className="rounded-2xl w-full h-auto object-contain"
              />
            </div>
          </div>
          <div>
            <p className="text-sm uppercase tracking-[0.2em] text-[#556B2F] mb-5 flex items-center gap-3 font-semibold">
              <span className="w-8 h-px bg-[#556B2F]"></span> Meet your coach
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-[#33311F] leading-[1.1]">
              Discipline built in the Army, <span className="text-[#556B2F] italic font-medium">brought to your corner.</span>
            </h2>
            <div className="mt-6 space-y-4 text-[#5C5848] leading-relaxed">
              <p>
                Hi, I'm <strong className="text-[#33311F]">Ina Cannon</strong>, founder of InAlign Wellness. As a U.S. Army
                veteran, I learned that discipline isn't about being perfect — it's about showing up and staying
                consistent, especially when motivation runs out.
              </p>
              <p>
                I specialize in <strong className="text-[#33311F]">body recomposition</strong> — helping you build lean
                muscle and burn body fat at the same time, so you don't just lose weight, you transform how your body
                looks, feels, and performs. I work virtually with clients all across the country.
              </p>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {[
                { icon: BadgeCheck, text: "Certified Health & Wellness Coach" },
                { icon: Dumbbell, text: "Certified Personal Trainer" },
                { icon: GraduationCap, text: "M.S. Exercise Science & Nutrition" },
                { icon: Shield, text: "U.S. Army Veteran" },
              ].map((c) => (
                <div key={c.text} className="flex items-center gap-3 bg-white border border-[#DAD0BA] rounded-xl px-4 py-3 shadow-[0_4px_16px_rgba(60,55,40,0.05)]">
                  <div className="w-9 h-9 rounded-full bg-[#556B2F]/12 flex items-center justify-center shrink-0">
                    <c.icon className="w-5 h-5 text-[#556B2F]" strokeWidth={1.75} />
                  </div>
                  <span className="text-sm font-medium text-[#33311F] leading-tight">{c.text}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 text-center">
              <p className="font-serif text-2xl italic text-[#C0714A] leading-none">Ina Cannon</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#5C5848] mt-1.5 font-semibold">Founder · InAlign Wellness</p>
              <p className="text-sm text-[#556B2F] mt-3 font-medium">Virtual coaching — train from anywhere, with clients across the country.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coaching banner */}
      <section className="max-w-5xl mx-auto px-6 pt-6 pb-4">
        <div className="rounded-3xl overflow-hidden border border-[#DAD0BA] shadow-[0_18px_50px_rgba(60,55,40,0.12)]">
          <div className="aspect-[5/4] overflow-hidden">
            <img
              src="https://customer-assets-wrfwihn1.emergentagent.net/job_book-slot-5/artifacts/a4dd0d4d802f4ce8_inna%20coach-068.webp"
              alt="InAlign Wellness virtual coaching session"
              data-testid="coaching-banner"
              className="w-full h-full object-cover object-right"
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="max-w-6xl mx-auto px-6 pt-8 pb-12">
        <div className="mb-10 max-w-2xl text-center mx-auto">
          <p className="text-base uppercase tracking-[0.2em] text-[#C0714A] font-bold mb-3">What I offer</p>
          <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-[#33311F]">Transformation programs</h2>
          <p className="text-[#5C5848] mt-4">Every program starts with a free discovery call. Pricing is tailored to your goals and discussed on the call.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[...services]
            .sort((a, b) => {
              const rank = (n) => (/coaching/i.test(n) ? 0 : /4\s*month/i.test(n) ? 1 : /6\s*month/i.test(n) ? 2 : 3);
              return rank(a.name) - rank(b.name);
            })
            .map((s, i) => {
            const isFeatured = /coaching/i.test(s.name);
            return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`service-card-${s.id}`}
              className={`group relative flex flex-col bg-[#FFFFFF] rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(60,55,40,0.1)] ${isFeatured ? "border-2 border-[#556B2F] shadow-[0_12px_40px_rgba(85,107,47,0.16)]" : "border border-[#DAD0BA]"}`}
            >
              {isFeatured ? (
                <div
                  data-testid="best-value-badge"
                  className="absolute top-4 right-4 z-10 flex items-center gap-1.5 bg-[#556B2F] text-white rounded-full pl-2.5 pr-3 py-1 text-xs font-bold tracking-wide shadow-[0_6px_18px_rgba(60,55,40,0.3)]"
                >
                  <Sparkles className="w-3.5 h-3.5" /> Full coaching + community
                </div>
              ) : (
                <div
                  data-testid="vip-badge"
                  className="absolute top-4 right-4 z-10 flex items-center gap-1.5 bg-[#33311F] text-white rounded-full pl-2.5 pr-3 py-1 text-xs font-bold tracking-wide shadow-[0_6px_18px_rgba(60,55,40,0.3)]"
                >
                  <Star className="w-3.5 h-3.5 fill-[#C0714A] text-[#C0714A]" /> VIP
                </div>
              )}
              <div className="h-48 overflow-hidden">
                <img src={s.image_url} alt={s.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-[#33311F]">{s.name}</h3>
                <p className="text-sm text-[#5C5848] mt-2 leading-relaxed">{s.description}</p>
                <ul className="mt-4 space-y-1.5">
                  {(isFeatured
                    ? ["Personalized plan + weekly 1:1 feedback from me", "Private community & 3 live Zoom calls a month", "Built around your goals, body & lifestyle"]
                    : ["Custom workout plan", "Nutrition tailored to you", "Weekly accountability"]
                  ).map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-[#33311F]">
                      <CheckCircle2 className="w-4 h-4 text-[#556B2F] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                {SERVICE_DETAILS[s.name] && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <button data-testid="see-included-btn" className="mt-4 text-sm font-semibold text-[#556B2F] hover:text-[#44531F] inline-flex items-center gap-1.5 transition-colors">
                        See everything that's included <ArrowRight className="w-4 h-4" />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-[#FBF7EE] border-[#DAD0BA]">
                      <DialogHeader>
                        <DialogTitle className="font-serif text-3xl text-[#33311F]">{s.name}</DialogTitle>
                      </DialogHeader>
                      <p className="text-sm font-semibold text-[#C0714A] uppercase tracking-wide">{SERVICE_DETAILS[s.name].tagline}</p>
                      {SERVICE_DETAILS[s.name].overview.map((p, idx) => (
                        <p key={idx} className="text-sm text-[#5C5848] leading-relaxed">{p}</p>
                      ))}
                      <div>
                        <p className="font-serif text-lg text-[#33311F] mb-3">What's included</p>
                        <ul className="space-y-2">
                          {SERVICE_DETAILS[s.name].includes.map((f) => (
                            <li key={f} className="flex items-start gap-2.5 text-sm text-[#33311F]">
                              <CheckCircle2 className="w-4 h-4 text-[#556B2F] shrink-0 mt-0.5" /> {f}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <p className="text-sm text-[#5C5848] leading-relaxed italic">{SERVICE_DETAILS[s.name].closing}</p>
                      <p className="text-sm text-[#33311F] font-medium">{SERVICE_DETAILS[s.name].note}</p>
                      <a
                        href={CAL_BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="dialog-book-btn"
                        className="mt-1 w-full inline-flex items-center justify-center gap-2 bg-[#556B2F] hover:bg-[#44531F] text-white rounded-full px-6 py-3 text-sm font-bold transition-colors"
                      >
                        Book a Discovery Call <ArrowRight className="w-4 h-4" />
                      </a>
                    </DialogContent>
                  </Dialog>
                )}
                <div className="flex items-center justify-between mt-auto pt-5 border-t border-[#DAD0BA]">
                  <span className="flex items-center gap-1.5 text-sm text-[#5C5848]">
                    <Clock className="w-4 h-4" /> {s.duration_minutes} min
                  </span>
                  {s.price > 0 ? (
                    <span className="font-serif text-xl text-[#33311F]">${s.price}</span>
                  ) : (
                    <span className="text-sm text-[#556B2F]">Free discovery call</span>
                  )}
                </div>
                <a
                  href={CAL_BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`book-service-${s.id}`}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 border border-[#556B2F] text-[#33311F] hover:bg-[#556B2F] hover:text-white rounded-full px-6 py-2.5 text-sm transition-colors"
                >
                  Book a discovery call <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
            );
          })}
        </div>
      </section>

      {/* Before & After / Results */}
      <section id="results" className="max-w-6xl mx-auto px-6 pt-6 pb-12">
        <div className="mb-10 max-w-2xl text-center mx-auto">
          <p className="text-base uppercase tracking-[0.2em] text-[#C0714A] font-bold mb-3">Before &amp; After</p>
          <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-[#33311F]">Real results, real discipline</h2>
          <p className="text-[#5C5848] mt-4">Consistency over perfection. Here's what showing up week after week can do — your transformation could be next.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {[
            { img: "https://customer-assets-wrfwihn1.emergentagent.net/job_book-slot-5/artifacts/5b866aed3546954b_InAlign_Testimonial_3_Front_Clean.jpg", result: "Lost 12 lbs and built real consistency — and just getting started on building muscle." },
            { img: "https://customer-assets-wrfwihn1.emergentagent.net/job_book-slot-5/artifacts/e7e34ebc8a6171d4_InAlign_Testimonial_1_Back_Clean.jpg", result: "The scale barely moved — but look at the muscle definition and drop in body fat. Proof the scale isn't everything." },
          ].map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              data-testid={`result-card-${i}`}
              className="bg-white border border-[#DAD0BA] rounded-3xl overflow-hidden shadow-[0_4px_16px_rgba(60,55,40,0.05)]"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img src={t.img} alt={`Client transformation ${i + 1}`} className="w-full h-full object-cover object-top" />
              </div>
              <div className="p-6">
                <p className="text-sm text-[#5C5848] leading-relaxed">{t.result}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <a
            href={CAL_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="results-cta-button"
            className="inline-flex items-center justify-center gap-2 bg-[#556B2F] hover:bg-[#44531F] text-white rounded-full px-9 py-4 text-base font-bold shadow-[0_8px_24px_rgba(85,107,47,0.25)] transition-all hover:-translate-y-0.5"
          >
            Start your transformation <ArrowRight className="w-4 h-4" />
          </a>
          <p className="text-sm text-[#5C5848] mt-3">Book your free 30-minute discovery call — your before photo starts today.</p>
        </div>
      </section>
      <section id="guide" className="max-w-6xl mx-auto px-6 py-12">
        <div className="bg-[#33311F] rounded-3xl overflow-hidden grid lg:grid-cols-12">
          <div className="lg:col-span-3 p-8 flex items-center justify-center bg-white/5">
            <img
              src="/discipline-code-cover.png"
              alt="The Discipline Code guide cover"
              data-testid="guide-cover"
              className="rounded-lg shadow-[0_18px_50px_rgba(0,0,0,0.45)] w-full max-w-[240px] rotate-[-3deg]"
            />
          </div>
          <div className="lg:col-span-5 p-10 lg:py-14 flex flex-col justify-center">
            <p className="text-sm uppercase tracking-[0.2em] text-[#556B2F] mb-4 flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Free Guide
            </p>
            <h2 className="font-serif text-4xl lg:text-5xl font-light text-white leading-tight">The Discipline Code</h2>
            <p className="text-white/80 mt-5 leading-relaxed">
              10 mantras to build discipline without relying on motivation, stay consistent when life gets busy, and
              create sustainable fat loss and lifelong health.
            </p>
            <ul className="mt-6 space-y-2 text-white/85 text-sm">
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#556B2F]" /> Build discipline without motivation</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#556B2F]" /> Stay consistent even when life gets busy</li>
              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#556B2F]" /> Sustainable fat loss and lifelong health</li>
            </ul>
          </div>
          <div className="lg:col-span-4 bg-white/5 p-10 lg:py-14 flex items-center">
            {guideDone ? (
              <div data-testid="guide-success" className="text-center w-full">
                <div className="w-14 h-14 rounded-full bg-[#556B2F] flex items-center justify-center mx-auto mb-5">
                  <CheckCircle2 className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-serif text-2xl text-white">Your guide is on its way!</h3>
                <p className="text-white/75 mt-3 text-sm">The download should have opened in a new tab, and I've emailed you a copy too. Check your inbox (and spam, just in case).</p>
                <a
                  href={guideDownloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="guide-manual-download"
                  className="mt-6 inline-flex items-center gap-2 bg-[#556B2F] hover:bg-[#44531F] text-white rounded-full px-6 py-3 text-sm transition-colors"
                >
                  <Download className="w-4 h-4" /> Download again
                </a>
              </div>
            ) : (
              <form onSubmit={submitGuide} className="w-full space-y-4" data-testid="guide-form">
                <p className="text-white font-medium">Enter your details to get instant access:</p>
                <Input
                  data-testid="guide-name"
                  required
                  placeholder="First name"
                  value={guide.name}
                  onChange={(e) => setGuide({ ...guide, name: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
                <Input
                  data-testid="guide-email"
                  required
                  type="email"
                  placeholder="Email address"
                  value={guide.email}
                  onChange={(e) => setGuide({ ...guide, email: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                />
                <button
                  type="submit"
                  data-testid="guide-submit"
                  disabled={guideSubmitting}
                  className="w-full bg-[#556B2F] hover:bg-[#44531F] disabled:opacity-50 text-white rounded-full py-3 transition-colors flex items-center justify-center gap-2"
                >
                  {guideSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download the guide
                </button>
                <p className="text-white/60 text-xs text-center leading-relaxed">
                  You'll also get my short 3-part discipline email series, straight from Ina. No spam — unsubscribe anytime.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="max-w-6xl mx-auto px-6 py-12">
        <div className="mb-12 max-w-xl">
          <p className="text-base uppercase tracking-[0.2em] text-[#C0714A] font-bold mb-3">Client love</p>
          <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-[#33311F]">What clients are saying</h2>
          <p className="text-[#5C5848] mt-4">Real reviews from people building stronger, healthier, more consistent lives with InAlign Wellness.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              data-testid={`testimonial-${i}`}
              className="bg-[#FFFFFF] border border-[#DAD0BA] rounded-3xl p-7 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(60,55,40,0.08)]"
            >
              <Quote className="w-7 h-7 text-[#556B2F] mb-4" strokeWidth={1.5} />
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="w-4 h-4 fill-[#C0714A] text-[#C0714A]" />
                ))}
              </div>
              <p className="text-[#5C5848] text-sm leading-relaxed flex-1">{t.text}</p>
              <div className="mt-6 pt-5 border-t border-[#DAD0BA] flex items-center justify-between">
                <span className="font-serif text-lg text-[#33311F]">{t.name}</span>
                <span className="text-xs uppercase tracking-[0.15em] text-[#556B2F]">Google Review</span>
              </div>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 text-center">
          <a
            href={CAL_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-cta-button"
            className="inline-flex items-center justify-center gap-2 bg-[#556B2F] hover:bg-[#44531F] text-white rounded-full px-9 py-4 text-base font-bold shadow-[0_8px_24px_rgba(85,107,47,0.25)] transition-all hover:-translate-y-0.5"
          >
            Become the next success story <ArrowRight className="w-4 h-4" />
          </a>
          <p className="text-sm text-[#5C5848] mt-3">Book your free 30-minute discovery call today.</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 py-12">
        <div className="bg-[#556B2F] rounded-3xl px-10 py-14 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl font-light text-white">Ready to begin?</h2>
          <p className="text-white/85 mt-4 max-w-md mx-auto">Book a free discovery call and let's build a plan that keeps you fit for life.</p>
          <a
            href={CAL_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-book-button"
            className="mt-8 inline-flex items-center gap-2 bg-white text-[#33311F] rounded-full px-8 py-3.5 transition-all hover:-translate-y-0.5"
          >
            Book a Discovery Call <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
